// src/components/CustomMarkdown/CustomChatMessages.jsx

import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import styles from './MarkdownContent.module.css';

import { CitationLink } from '../../hooks/useOpenCitation';
import { decorateMarkdown, sanitizeInternalTags } from '../../utils/citationService';

// Import libraries for math and syntax highlighting
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * Extract tables from markdown content
 * Separated from component logic for better testability and reusability
 */
function extractTablesFromMarkdown(text) {
  const tableRegex = /^(\|.+\|)\s*\n(\|[-|]+\|)\s*\n((?:\|.+\|(?:\n|$))+)/gm;
  const parts = [];
  let lastIndex = 0;
  
  // Early return if no tables
  if (!text || !text.includes('|')) {
    return [{ type: 'text', content: text || '' }];
  }
  
  let match;
  while ((match = tableRegex.exec(text)) !== null) {
    // Add text before the table
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index)
      });
    }
    
    // Extract table components
    parts.push({
      type: 'table',
      header: match[1],
      separator: match[2],
      body: match[3]
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add any remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex)
    });
  }
  
  return parts;
}

/**
 * Parse table cells from raw markdown table strings
 */
function parseTableCells(tableData) {
  const headerCells = tableData.header
    .split('|')
    .filter(cell => cell.trim() !== '')
    .map(cell => cell.trim());
    
  const bodyRows = tableData.body
    .split('\n')
    .filter(row => row.trim() !== '')
    .map(row => 
      row.split('|')
         .filter(cell => cell.trim() !== '')
         .map(cell => cell.trim())
    );
    
  return { headerCells, bodyRows };
}

/**
 * A component that renders Markdown tables directly without relying on ReactMarkdown
 */
const TableFormatter = ({ content, components, reference, onCitationClick }) => {
  const tableFormatterRef = useRef(null);
  
  // Split content into parts with and without tables
  const parts = useMemo(() => extractTablesFromMarkdown(content), [content]);
  
  // Render the table directly with HTML
  const renderTable = (table) => {
    const { headerCells, bodyRows } = parseTableCells(table);
    
    return (
      <div className={styles.tableContainer}>
        <table className={styles.enhancedTable}>
          <thead>
            <tr>
              {headerCells.map((cell, i) => (
                <th key={i} dangerouslySetInnerHTML={{ __html: cell }} />
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j} dangerouslySetInnerHTML={{ __html: cell }} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  // Render the content
  return (
    <div className={styles.tableFormatterContainer} ref={tableFormatterRef}>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {part.type === 'text' ? (
            // Use ReactMarkdown for non-table content with all the custom components
            <ReactMarkdown 
              remarkPlugins={[remarkGfm, remarkMath, remarkBreaks]}
              rehypePlugins={[rehypeRaw, rehypeKatex]}
              components={{...components, reference }}
            >
              {part.content}
            </ReactMarkdown>
          ) : (
            // Use our custom renderer for tables
            renderTable(part)
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Markdown processing pipeline functions separated for better testability and composability
 */
const markdownProcessors = {
  preprocessLatex: (text) => {
    if (!text) return '';
    return text.replace(/\\\[(.*?)\\\]/gs, '$$$1$$');
  },

  /**
   * Collapse multiple consecutive newlines to improve formatting
   */
  collapseBlankLines: (text) => {
    if (!text) return '';
    return text.replace(/\n{3,}/g, '\n\n');
  },

  /**
   * Clean up newlines specifically around table markers
   */
  cleanupTables: (text) => {
    if (!text) return '';
    
    return text
      .replace(/\n{2,}(\|[^\n]*\|)/g, '\n$1')
      .replace(/(#{1,6}[^\n]+)\n{2,}(\|[^\n]*\|)/g, '$1\n$2')
      .replace(/([^\n|]+)\n{2,}(\|[^\n]*\|)/g, '$1\n$2')
      .replace(/(\|[^\n]*\|)\n{2,}(\|[^\n]*\|)/g, '$1\n$2');
  },

  /**
   * Detect if content contains Markdown tables
   */
  hasTables: (text) => {
    if (!text) return false;
    return /\|.+\|\s*\n\|[-|]+\|/.test(text);
  },

  /**
   * Apply all processors in sequence
   */
  pipeline: (text) => {
    let processed = sanitizeInternalTags(text);
    processed = markdownProcessors.preprocessLatex(processed);
    processed = decorateMarkdown(processed);
    processed = markdownProcessors.collapseBlankLines(processed);
    processed = markdownProcessors.cleanupTables(processed);
    return processed;
  }
};

/**
 * Custom hook to handle post-render table styling separated from component for better testability
 */
function useTableStyling(content, containerRef) {
  useEffect(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const tables = container.querySelectorAll('table');
    
    // Apply styling to tables and their preceding elements
    tables.forEach(table => {
      const prevElement = table.previousElementSibling;
      if (prevElement && (prevElement.tagName === 'P' || prevElement.tagName === 'DIV')) {
        prevElement.classList.add(styles.preTableParagraph);
        table.classList.add(styles.enhancedTable);
        
        if (table.parentElement?.tagName === 'DIV') {
          table.parentElement.classList.add(styles.tableWrapper);
        }
      }
    });
  }, [content]);
}

/**
 * Chat Message component that renders markdown with citation support
 */
function CustomChatMessage({ content, reference, onCitationClick, className }) {
  const containerRef = useRef(null);

  // Apply table styling after render
  useTableStyling(content, containerRef);

  // Process content through our pipeline
  const processedContent = useMemo(() => markdownProcessors.pipeline(content), [content]);

  // Determine if we should use TableFormatter
  const hasTables = useMemo(() => markdownProcessors.hasTables(processedContent), [processedContent]);

  // Code rendering component - separated for clarity
  const CodeRenderer = useCallback(({ node, inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : null;
    const codeString = String(children).replace(/\n$/, '');

    const handleCopy = useCallback(() => {
      navigator.clipboard.writeText(codeString)
        .then(() => {
        })
        .catch(err => {
          console.error('Failed to copy code: ', err);
          alert('Failed to copy code to clipboard.');
        });
    }, [codeString]);

    if (inline) {
      return (
        <code className={styles.inlineCode} {...props}>
          {children}
        </code>
      );
    }

    // Block code with syntax highlighting and copy button
    return (
      <div className={styles.codeBlockWrapper}>
        <button className={styles.copyButton} onClick={handleCopy}>
          Copy
        </button>
        {language ? (
          <SyntaxHighlighter
            style={oneDark}
            language={language}
            PreTag="div"
            showLineNumbers
            wrapLongLines
            {...props}
          >
            {codeString}
          </SyntaxHighlighter>
        ) : (
          <pre className={styles.codeBlock} {...props}>
            <code>{codeString}</code>
          </pre>
        )}
      </div>
    );
  }, []);

  // Customize components passed to ReactMarkdown
  const components = {
    // Map the injected <Citation> tag to our component
    citation: ({ index }) => (
      <CitationLink index={Number(index)} reference={reference} />
    ),
    
    // Use the separated code renderer
    code: CodeRenderer,
    
    // --- Custom Table Renderer --- 
    table({ node, ...props }) {
      return (
        <div className={styles.tableContainer}>
          <table className={styles.enhancedTable} {...props} />
        </div>
      );
    },
    
    // --- Custom Paragraph Renderer ---
    // This helps control whitespace around elements
    p({ node, children, ...props }) {
      // Enhanced check for empty paragraphs that might contain whitespace or br tags
      const childArray = React.Children.toArray(children);
      
      // Check if it's actually empty or just whitespace/br
      const isEmpty = childArray.length === 0 || 
                      (childArray.length === 1 && 
                       (childArray[0] === '\n' || 
                        childArray[0] === ' ' || 
                        childArray[0] === '\t' || 
                        (typeof childArray[0] === 'object' && 
                         React.isValidElement(childArray[0]) && 
                         childArray[0].type === 'br')));
      
      if (isEmpty) {
        return null;
      }
      
      return <p {...props}>{children}</p>;
    },
    
    // --- Existing Link Renderer --- 
    a: ({ node, href, children, ...props }) => {
      if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
        return (
          <a 
            href={href} 
            target="_blank"
            rel="noopener noreferrer"
            {...props}
          >
            {children}
          </a>
        );
      }
      return <a href={href} {...props}>{children}</a>;
    },
  };
  
  // Use the TableFormatter for content with tables to avoid ReactMarkdown's table issues
  if (hasTables) {
    return (
      <div className={`${className} ${styles.markdownContainer}`} ref={containerRef}> 
        <TableFormatter content={processedContent} components={components} reference={reference} onCitationClick={onCitationClick} />
      </div>
    );
  }
  
  // Otherwise, use ReactMarkdown as usual
  return (
    // Wrap ReactMarkdown in a div with a dedicated class for styling
    <div className={`${className} ${styles.markdownContainer}`} ref={containerRef}> 
      <ReactMarkdown
        // Added plugins for markdown processing
        remarkPlugins={[remarkGfm, remarkMath, remarkBreaks]}
        // Added katex plugin (ensure rehypeRaw is after katex if issues arise)
        rehypePlugins={[rehypeRaw, rehypeKatex]} 
        components={components}
      >
        {/* Use the fully processed content */} 
        {processedContent}
      </ReactMarkdown>
    </div>
  );
}

export default CustomChatMessage; 